import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from './firebase';
import { recordActivity } from './activityDayService';
import type { ReflectionType } from '../types';

/**
 * Persists a user's reflection on a transaction to Firestore.
 * Stored on the plaidTransactions document so it survives Plaid sync updates
 * (sync uses merge=True and does not overwrite user-added fields).
 *
 * When `uid` is provided, also marks today's activityDay as active (presence
 * flag — see recordActivity) so a reflect-only day still counts toward
 * totalActivities / streaks. The per-day reflection *volume* is computed
 * server-side from `plaidTransactions.reflection`, not from this flag.
 */
export async function updateTransactionReflectionInFirestore(
    transactionDocId: string,
    reflection: ReflectionType,
    uid?: string
): Promise<void> {
    const txRef = doc(db, 'plaidTransactions', transactionDocId);
    await updateDoc(txRef, {
        reflection,
        reflectionSource: 'user',
        reflectionUpdatedAt: new Date().toISOString(),
    });

    if (uid) {
        // Fire-and-forget: session-cached, so a flurry of reflections writes once.
        recordActivity(uid, 'reflections').catch(() => {});
    }
}

/**
 * Persists a user note on a transaction to Firestore.
 * Stored on the plaidTransactions document so it survives Plaid sync updates
 * (sync uses merge=True and does not overwrite user-added fields).
 */
export async function updateTransactionNoteInFirestore(
    transactionDocId: string,
    note: string
): Promise<void> {
    const txRef = doc(db, 'plaidTransactions', transactionDocId);
    await updateDoc(txRef, { userNote: note });
}

/**
 * Persists a per-transaction category override to Firestore.
 * Stored on the plaidTransactions document so it survives Plaid sync updates.
 */
export async function updateTransactionCategoryInFirestore(
    transactionDocId: string,
    categoryDetailedCode: string,
): Promise<void> {
    const txRef = doc(db, 'plaidTransactions', transactionDocId);
    await updateDoc(txRef, { userCategoryOverride: categoryDetailedCode });
}

/**
 * Removes the per-transaction category override from Firestore.
 */
export async function removeTransactionCategoryOverride(
    transactionDocId: string,
): Promise<void> {
    const txRef = doc(db, 'plaidTransactions', transactionDocId);
    await updateDoc(txRef, { userCategoryOverride: deleteField() });
}
